import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FileText, MessageCircle, Loader2, ExternalLink, ChevronDown } from "lucide-react";

const formatINR = (n) =>
  typeof n === "number" ? `INR ${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "";

export const InquiryReplyDialog = ({ open, onOpenChange, inquiry, onReplied }) => {
  const [receipts, setReceipts] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attach, setAttach] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const selected = useMemo(
    () => receipts.find((r) => r.order_id === selectedOrderId) || receipts[0] || null,
    [receipts, selectedOrderId],
  );

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setPickerOpen(false);
    setMessage(
      `Hi ${inquiry?.name || "there"}, thanks for your interest in ${inquiry?.property_title || "the property"}. ` +
        `Happy to share more details or schedule a visit — when works for you?`,
    );
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/me/receipts");
        const list = Array.isArray(data) ? data : [];
        setReceipts(list);
        setSelectedOrderId(list[0]?.order_id || null);
        if (!list.length) setAttach(false);
      } catch {
        setReceipts([]);
        setAttach(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, inquiry]);

  const send = async () => {
    if (!message.trim()) {
      toast.error("Write a short reply first");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/inquiries/${inquiry.id}/reply`, {
        message: message.trim(),
        attach_receipt: attach,
        order_id: attach ? selectedOrderId : null,
      });
      setResult(data);
      if (data.wa_me_url) window.open(data.wa_me_url, "_blank", "noopener,noreferrer");
      toast.success(
        data?.whatsapp?.mock
          ? "Reply drafted. WhatsApp opened — tap send."
          : "Reply sent via WhatsApp.",
      );
      onReplied?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="inquiry-reply-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[var(--teak)]">
            Reply to {inquiry?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl border border-[var(--line)] bg-[var(--cream)]/40 p-3 text-xs text-[var(--ink-muted)]">
            <div className="font-serif text-sm text-[var(--teak)]">{inquiry?.property_title}</div>
            <div className="italic mt-1">&ldquo;{inquiry?.message}&rdquo;</div>
            <div className="mt-2">To: {inquiry?.phone}</div>
          </div>

          <div className="rounded-xl border border-[var(--line)] p-3" data-testid="invoice-attachment-block">
            <div className="flex items-start justify-between gap-3">
              <div className="flex gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-[var(--primary)]/10 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-[var(--primary)]" />
                </div>
                <div className="min-w-0 flex-1">
                  {loading ? (
                    <div className="text-sm text-[var(--ink-muted)]">Loading tax invoices…</div>
                  ) : selected ? (
                    <>
                      <button
                        type="button"
                        onClick={() => receipts.length > 1 && setPickerOpen((v) => !v)}
                        className={`flex items-center gap-1 font-serif text-base text-[var(--teak)] truncate ${
                          receipts.length > 1 ? "hover:text-[var(--primary)]" : "cursor-default"
                        }`}
                        data-testid="invoice-attachment-no"
                      >
                        {selected.invoice_no}
                        {receipts.length > 1 && <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                      <div className="text-xs text-[var(--ink-muted)]">
                        {selected.purpose_label} · {formatINR(selected.amount_inr)}
                        {selected.mock && <span className="chip text-[10px] ml-2">test</span>}
                      </div>
                      <a
                        href={selected.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[var(--primary)] inline-flex items-center gap-1 mt-1"
                        data-testid="invoice-attachment-preview"
                      >
                        Preview invoice <ExternalLink className="w-3 h-3" />
                      </a>

                      {pickerOpen && receipts.length > 1 && (
                        <div
                          className="mt-2 border border-[var(--line)] rounded-lg divide-y divide-[var(--line)] bg-white max-h-48 overflow-auto"
                          data-testid="invoice-picker"
                        >
                          {receipts.map((r) => (
                            <button
                              key={r.order_id}
                              type="button"
                              onClick={() => {
                                setSelectedOrderId(r.order_id);
                                setPickerOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-xs hover:bg-[var(--cream)]/50 ${
                                r.order_id === selectedOrderId ? "bg-[var(--cream)]/60" : ""
                              }`}
                              data-testid={`invoice-picker-option-${r.order_id}`}
                            >
                              <div className="font-medium text-[var(--teak)]">{r.invoice_no}</div>
                              <div className="text-[var(--ink-muted)]">
                                {r.purpose_label} · {formatINR(r.amount_inr)} ·{" "}
                                {r.paid_at ? new Date(r.paid_at).toLocaleDateString() : ""}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm">
                      <div className="text-[var(--teak)] font-medium">No paid tax invoice yet</div>
                      <div className="text-xs text-[var(--ink-muted)]">
                        Pay for a verification tier to auto-attach invoices to replies.
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Switch
                checked={attach && !!selected}
                disabled={!selected || loading}
                onCheckedChange={setAttach}
                data-testid="invoice-attach-toggle"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--teak)]">Your reply</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="mt-1"
              placeholder="Write a quick, warm reply…"
              data-testid="inquiry-reply-message"
            />
          </div>

          {result && (
            <div
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900"
              data-testid="inquiry-reply-result"
            >
              {result.attached_invoice_no ? (
                <>
                  Reply drafted with Tax Invoice <b>{result.attached_invoice_no}</b>.
                </>
              ) : (
                <>Reply drafted on WhatsApp.</>
              )}{" "}
              {result?.whatsapp?.mock
                ? "Twilio is in test mode — the message auto-opens on WhatsApp web for you to tap send."
                : "Delivered via Twilio WhatsApp."}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <button
            className="btn-ghost"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            data-testid="inquiry-reply-cancel"
          >
            Close
          </button>
          <button
            className="btn-primary inline-flex items-center gap-2"
            onClick={send}
            disabled={sending || !message.trim()}
            data-testid="inquiry-reply-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
            Send via WhatsApp
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
